import { useEffect } from 'react';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import { useRegisterSharedChartData } from '../../SharedReport/SharedReportDataContext';
import type { UpdateChartContentParams } from '../../TextChartContentProvider/TextChartContentProvider';
import { TextChartContent } from './TextChartContent';
import { useProcessContent } from './utils/use-process-content';

type TextRendererProps = {
  content: string;
  multiData?: MultiMetricDataMap;
  color?: string;
  vAlign?: NonNullable<PositionedItem['plot']>['textVAlign'];
  item: PositionedItem;
  onProcessedContent: (params: UpdateChartContentParams) => void;
  isLoading?: boolean;
};
export const TextRenderer = ({
  content,
  multiData,
  color,
  vAlign,
  item,
  onProcessedContent,
  isLoading: externalIsLoading,
}: TextRendererProps) => {
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const { processedContent, error } = useProcessContent({
    content,
    multiData,
  });

  useRegisterSharedChartData(item.id, {
    markdown: processedContent ?? undefined,
  });

  const isLoading = externalIsLoading ?? processedContent === null;

  useEffect(() => {
    if (processedContent !== null) {
      const isEmpty =
        typeof processedContent === 'string' && processedContent.trim() === '';
      onProcessedContent({
        chartId: item.id,
        content: processedContent,
        isLoading: false,
        error: isEmpty ? 'Template produced no visible content' : null,
      });
    } else if (error) {
      onProcessedContent({
        chartId: item.id,
        content: null,
        isLoading: false,
        error,
      });
    } else {
      onProcessedContent({
        chartId: item.id,
        content: null,
        isLoading,
        error: null,
      });
    }
  }, [processedContent, error, isLoading, item.id, onProcessedContent]);

  useEffect(() => {
    return () => {
      onProcessedContent({
        chartId: item.id,
        content: null,
        isLoading: false,
        error: null,
      });
    };
  }, [item.id, onProcessedContent]);

  return (
    <TextChartContent
      content={processedContent}
      color={color}
      vAlign={vAlign}
      isGlobalAiChartPreview={isGlobalAiChartPreview}
    />
  );
};
